import { Input, Select, Space } from 'antd';
import { useMemo } from 'react';
import { useFieldDefaults } from './defaults';
import { normalize } from './normalize';

/**
 * Country-code Select + national-number Input. Emits a single dial-formatted
 * string like `+91 98450 12345`. The widget preserves user-typed spacing
 * because Indian phone numbers are read out in 5-5 groups over the phone —
 * a "helpful" reformatter would fight the caller. Zod at the API boundary
 * enforces E.164; the widget's job is to keep the human's copy legible.
 *
 * Country-code list is deliberately short. Add via `PHONE_COUNTRIES` when a
 * customer needs one; a hundred-item dropdown is not the answer.
 */
const PHONE_COUNTRIES = [
  { code: '+91', label: '+91 IN' },
  { code: '+1', label: '+1 US' },
  { code: '+44', label: '+44 UK' },
  { code: '+971', label: '+971 AE' },
  { code: '+65', label: '+65 SG' },
];

interface Props {
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  status?: 'error' | 'warning';
  id?: string;
}

function split(value: string | undefined, fallbackCountry: string): { country: string; national: string } {
  if (!value) return { country: fallbackCountry, national: '' };
  const match = PHONE_COUNTRIES.find((c) => value.startsWith(c.code));
  if (match) return { country: match.code, national: value.slice(match.code.length).trimStart() };
  return { country: fallbackCountry, national: value };
}

export function PhoneField({ value, onChange, onBlur, disabled, autoFocus, placeholder, status, id }: Props) {
  const defaults = useFieldDefaults();
  const fallbackCountry = defaults.phone?.defaultCountry ?? '+91';
  const { country, national } = useMemo(() => split(value, fallbackCountry), [value, fallbackCountry]);

  const emit = (nextCountry: string, nextNational: string) => {
    const cleaned = nextNational.replace(/^\s+/, '');
    if (!cleaned) {
      onChange?.(undefined);
      return;
    }
    onChange?.(`${nextCountry} ${cleaned}`);
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        value={country}
        onChange={(c) => emit(c, national)}
        options={PHONE_COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
        style={{ width: 100 }}
        disabled={disabled}
      />
      <Input
        id={id}
        value={national}
        onChange={(e) => emit(country, e.target.value)}
        onBlur={() => {
          const collapsed = normalize(national, ['collapseSpaces']);
          if (collapsed !== national) emit(country, collapsed);
          onBlur?.();
        }}
        placeholder={placeholder ?? '98450 12345'}
        disabled={disabled}
        autoFocus={autoFocus}
        status={status}
        inputMode="tel"
      />
    </Space.Compact>
  );
}
